import { useState, useEffect } from "react";
import { Badge } from "../ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { fetchAnnualWheel } from "../../services/businessDashApi";
import { cn } from "../../lib/utils";

// ─── Filter type constants (mirrors Lovable's TypeScript enums) ─────────────
export const FILTER_TYPES = { YEAR: "year", MONTH: "month", QUARTER: "quarter" };
export const DEFAULT_FILTER = { type: FILTER_TYPES.YEAR, value: "all" };

// Segment id → month number (ring 3)
const MONTH_MAP = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

// Segment id → quarter number (ring 1)
const QUARTER_MAP = { q1: 1, q2: 2, q3: 3, q4: 4 };

// Determine if a segment is highlighted by the active filter
const isSegmentActive = (segment, activeFilter) => {
  if (!activeFilter || activeFilter.type === FILTER_TYPES.YEAR) return false;

  if (activeFilter.type === FILTER_TYPES.QUARTER && segment.ring === 1) {
    return QUARTER_MAP[segment.id] === activeFilter.value;
  }
  if (activeFilter.type === FILTER_TYPES.MONTH && segment.ring === 3) {
    return MONTH_MAP[segment.id] === activeFilter.value;
  }
  // Dim the other rings when filtering
  return false;
};

const isDimmed = (segment, activeFilter) => {
  if (!activeFilter || activeFilter.type === FILTER_TYPES.YEAR) return false;
  if (activeFilter.type === FILTER_TYPES.QUARTER && segment.ring !== 1) return true;
  if (activeFilter.type === FILTER_TYPES.MONTH && segment.ring !== 3) return true;
  if (activeFilter.type === FILTER_TYPES.QUARTER && segment.ring === 1)
    return QUARTER_MAP[segment.id] !== activeFilter.value;
  if (activeFilter.type === FILTER_TYPES.MONTH && segment.ring === 3)
    return MONTH_MAP[segment.id] !== activeFilter.value;
  return false;
};

// ─── Static ring segment definitions ─────────────────────────────────────────

const RING_SEGMENTS = [
  // Ring 1 - Quarters (Yellow)
  { id: "q1", startAngle: 0,   endAngle: 90,  label: "Q1", type: "events",      dates: "Jan-Mar",  ring: 1 },
  { id: "q2", startAngle: 90,  endAngle: 180, label: "Q2", type: "events",      dates: "Apr-Jun",  ring: 1 },
  { id: "q3", startAngle: 180, endAngle: 270, label: "Q3", type: "events",      dates: "Jul-Sep",  ring: 1 },
  { id: "q4", startAngle: 270, endAngle: 360, label: "Q4", type: "events",      dates: "Oct-Dec",  ring: 1 },
  // Ring 2 - Bi-monthly (Pink)
  { id: "a1", startAngle: 0,   endAngle: 60,  label: "",   type: "assignments", dates: "Jan-Feb",  ring: 2 },
  { id: "a2", startAngle: 60,  endAngle: 120, label: "",   type: "assignments", dates: "Mar-Apr",  ring: 2 },
  { id: "a3", startAngle: 120, endAngle: 180, label: "",   type: "assignments", dates: "May-Jun",  ring: 2 },
  { id: "a4", startAngle: 180, endAngle: 240, label: "",   type: "assignments", dates: "Jul-Aug",  ring: 2 },
  { id: "a5", startAngle: 240, endAngle: 300, label: "",   type: "assignments", dates: "Sep-Oct",  ring: 2 },
  { id: "a6", startAngle: 300, endAngle: 360, label: "",   type: "assignments", dates: "Nov-Dec",  ring: 2 },
  // Ring 3 - Months (Blue)
  { id: "jan", startAngle: 0,   endAngle: 30,  label: "Jan", type: "restudy", dates: "January",   ring: 3 },
  { id: "feb", startAngle: 30,  endAngle: 60,  label: "Feb", type: "restudy", dates: "February",  ring: 3 },
  { id: "mar", startAngle: 60,  endAngle: 90,  label: "Mar", type: "restudy", dates: "March",     ring: 3 },
  { id: "apr", startAngle: 90,  endAngle: 120, label: "Apr", type: "restudy", dates: "April",     ring: 3 },
  { id: "may", startAngle: 120, endAngle: 150, label: "May", type: "restudy", dates: "May",       ring: 3 },
  { id: "jun", startAngle: 150, endAngle: 180, label: "Jun", type: "restudy", dates: "June",      ring: 3 },
  { id: "jul", startAngle: 180, endAngle: 210, label: "Jul", type: "restudy", dates: "July",      ring: 3 },
  { id: "aug", startAngle: 210, endAngle: 240, label: "Aug", type: "restudy", dates: "August",    ring: 3 },
  { id: "sep", startAngle: 240, endAngle: 270, label: "Sep", type: "restudy", dates: "September", ring: 3 },
  { id: "oct", startAngle: 270, endAngle: 300, label: "Oct", type: "restudy", dates: "October",   ring: 3 },
  { id: "nov", startAngle: 300, endAngle: 330, label: "Nov", type: "restudy", dates: "November",  ring: 3 },
  { id: "dec", startAngle: 330, endAngle: 360, label: "Dec", type: "restudy", dates: "December",  ring: 3 },
];

const MOCK_EVENTS = [
  { id: 1,  date: "2025-03-15", name: "Q1 Board Meeting",      type: "Board Meeting",  quarter: 1, month: 3,  attendees: ["Sarah Johnson", "Michael Brown", "Emily Davis", "Robert Wilson"], documents: ["Q1 Financial Report", "Strategic Review"] },
  { id: 2,  date: "2025-04-20", name: "Annual General Meeting",type: "Company Event",  quarter: 2, month: 4,  attendees: ["All Board Members", "Executive Team"], documents: ["AGM Presentation", "Annual Report"] },
  { id: 3,  date: "2025-06-01", name: "ESG Reporting",         type: "Sustainability", quarter: 2, month: 6,  attendees: ["ESG Committee", "External Auditor"], documents: ["ESG Report Draft"] },
  { id: 4,  date: "2025-06-15", name: "Q2 Board Meeting",      type: "Board Meeting",  quarter: 2, month: 6,  attendees: ["All Board Members"], documents: ["Q2 Review", "Strategy Update"] },
  { id: 5,  date: "2025-07-30", name: "Summer Strategy Review",type: "Company Event",  quarter: 3, month: 7,  attendees: ["Executive Team"], documents: ["Strategy Framework"] },
  { id: 6,  date: "2025-08-15", name: "Sustainability Workshop",type: "Sustainability",quarter: 3, month: 8,  attendees: ["All Board Members", "ESG Consultant"], documents: ["Workshop Materials"] },
  { id: 7,  date: "2025-09-15", name: "Q3 Board Meeting",      type: "Board Meeting",  quarter: 3, month: 9,  attendees: ["All Board Members"], documents: ["Q3 Financial Report"] },
  { id: 8,  date: "2025-10-10", name: "Sustainability Audit",  type: "Sustainability", quarter: 4, month: 10, attendees: ["ESG Committee", "External Auditor"], documents: ["Audit Framework"] },
  { id: 9,  date: "2025-12-15", name: "Q4 Board Meeting",      type: "Board Meeting",  quarter: 4, month: 12, attendees: ["All Board Members"], documents: ["Annual Review", "Budget Planning"] },
];

// ─── Geometry helpers ─────────────────────────────────────────────────────────

const getRingRadius  = (ring) => 40 + (ring - 1) * 30;
const getRingWidth   = ()     => 25;

const createSegmentPath = (segment) => {
  const cx = 160, cy = 160;
  const r1 = getRingRadius(segment.ring);
  const r2 = r1 + getRingWidth();
  const toRad = (deg) => (deg - 90) * (Math.PI / 180);
  const sa = toRad(segment.startAngle);
  const ea = toRad(segment.endAngle);
  const la = segment.endAngle - segment.startAngle > 180 ? 1 : 0;
  return [
    `M ${cx + r1 * Math.cos(sa)} ${cy + r1 * Math.sin(sa)}`,
    `L ${cx + r2 * Math.cos(sa)} ${cy + r2 * Math.sin(sa)}`,
    `A ${r2} ${r2} 0 ${la} 1 ${cx + r2 * Math.cos(ea)} ${cy + r2 * Math.sin(ea)}`,
    `L ${cx + r1 * Math.cos(ea)} ${cy + r1 * Math.sin(ea)}`,
    `A ${r1} ${r1} 0 ${la} 0 ${cx + r1 * Math.cos(sa)} ${cy + r1 * Math.sin(sa)}`,
  ].join(" ");
};

const getTextPosition = (segment) => {
  const cx = 160, cy = 160;
  const radius = getRingRadius(segment.ring) + getRingWidth() / 2;
  const mid = ((segment.startAngle + segment.endAngle) / 2 - 90) * (Math.PI / 180);
  return { x: cx + radius * Math.cos(mid), y: cy + radius * Math.sin(mid) };
};

/** Matches Lovable: pastel bases from theme; active fills per ring type. */
const getSegmentColor = (type, isActive) => {
  if (isActive) {
    if (type === "restudy") return "hsl(var(--primary))";
    if (type === "assignments") return "hsl(342 60% 60%)";
    if (type === "events") return "hsl(45 80% 50%)";
  }
  switch (type) {
    case "events":
      return "hsl(var(--pastel-yellow))";
    case "assignments":
      return "hsl(var(--pastel-pink))";
    case "restudy":
      return "hsl(var(--pastel-blue))";
    default:
      return "hsl(var(--muted))";
  }
};

// ─── WheelSVG sub-component ────────────────────────────────────────────────

const WheelSVG = ({ size, activeFilter, onFilterChange, eventsData }) => {
  const wheelSize = size === "large" ? 600 : 500;
  const scale = size === "large" ? 1.875 : 1.56;

  const getMeetingsForSegment = (segment) => {
    const all = MOCK_EVENTS;
    if (segment.ring === 1) {
      const q = QUARTER_MAP[segment.id];
      return all.filter((e) => e.quarter === q && e.type === "Board Meeting");
    }
    if (segment.ring === 2) {
      const biMonthlyMap = { a1:[1,2], a2:[3,4], a3:[5,6], a4:[7,8], a5:[9,10], a6:[11,12] };
      const months = biMonthlyMap[segment.id] || [];
      return all.filter((e) => months.includes(e.month) && e.type === "Sustainability");
    }
    if (segment.ring === 3) {
      const m = MONTH_MAP[segment.id];
      return all.filter((e) => e.month === m);
    }
    return [];
  };

  const handleSegmentClick = (segment) => {
    if (!onFilterChange) return;
    if (segment.ring === 1) {
      const q = QUARTER_MAP[segment.id];
      const already = activeFilter?.type === FILTER_TYPES.QUARTER && activeFilter?.value === q;
      onFilterChange(already ? DEFAULT_FILTER : { type: FILTER_TYPES.QUARTER, value: q });
    } else if (segment.ring === 3) {
      const m = MONTH_MAP[segment.id];
      const already = activeFilter?.type === FILTER_TYPES.MONTH && activeFilter?.value === m;
      onFilterChange(already ? DEFAULT_FILTER : { type: FILTER_TYPES.MONTH, value: m });
    }
  };

  const handleCenterClick = () => {
    if (onFilterChange) onFilterChange(DEFAULT_FILTER);
  };

  return (
    <div className="relative" style={{ width: wheelSize, height: wheelSize }}>
      <svg width={wheelSize} height={wheelSize} className="overflow-visible">
        {RING_SEGMENTS.map((segment) => {
          const meetings    = getMeetingsForSegment(segment);
          const active = isSegmentActive(segment, activeFilter);
          const dimmed = isDimmed(segment, activeFilter);
          const baseColor = getSegmentColor(segment.type, active);
          const isClickable = segment.ring === 1 || segment.ring === 3;

          const pathEl = (
            <path
              d={createSegmentPath(segment)}
              fill={baseColor}
              stroke={active ? "hsl(var(--primary))" : "white"}
              strokeWidth={active ? 2.5 : 1}
              opacity={dimmed ? 0.35 : 1}
              className={cn(
                "transition-all duration-200",
                isClickable && "cursor-pointer hover:opacity-80"
              )}
              onClick={isClickable ? () => handleSegmentClick(segment) : undefined}
            />
          );

          const pos = getTextPosition(segment);
          const labelEl = segment.label && (
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="font-medium fill-foreground pointer-events-none"
              style={{
                fontSize: size === "large" ? "10px" : "8px",
                fontWeight: active ? "bold" : "normal",
              }}
            >
              {segment.label}
            </text>
          );

          if (meetings.length === 0) {
            return (
              <g key={segment.id} transform={`scale(${scale})`}>
                {pathEl}
                {labelEl}
              </g>
            );
          }

          return (
            <HoverCard key={segment.id}>
              <HoverCardTrigger asChild>
                <g
                  transform={`scale(${scale})`}
                  className={cn(isClickable && "cursor-pointer")}
                  onClick={isClickable ? () => handleSegmentClick(segment) : undefined}
                >
                  {pathEl}
                  {/* Event dot indicator */}
                  <circle
                    cx={pos.x}
                    cy={pos.y - (size === "large" ? 10 : 8)}
                    r={size === "large" ? 4 : 3}
                    fill="hsl(var(--primary))"
                    opacity={dimmed ? 0.35 : 0.8}
                    className="pointer-events-none"
                  />
                  {labelEl}
                </g>
              </HoverCardTrigger>
              <HoverCardContent className="w-72">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">
                      {segment.ring === 1
                        ? `${segment.label} Meetings`
                        : segment.ring === 2
                        ? `${segment.dates} Events`
                        : `${segment.dates} Meetings`}
                    </h4>
                    <Badge variant="secondary" className="text-[10px]">
                      {meetings.length} event{meetings.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    {meetings.map((m) => (
                      <div
                        key={m.id}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span className="font-medium text-foreground">{m.name}</span>
                        <span>
                          {new Date(m.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                  {isClickable && (
                    <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                      Click to filter events
                    </p>
                  )}
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        })}

        {/* Center circle — click to reset filter */}
        <circle
          cx={160 * scale}
          cy={160 * scale}
          r={35 * scale}
          fill="hsl(var(--background))"
          stroke={activeFilter?.type !== FILTER_TYPES.YEAR ? "hsl(var(--primary))" : "hsl(var(--border))"}
          strokeWidth={activeFilter?.type !== FILTER_TYPES.YEAR ? 2.5 : 2}
          className="cursor-pointer hover:opacity-90 transition-opacity"
          onClick={handleCenterClick}
        />
        <text
          x={160 * scale}
          y={155 * scale}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-foreground font-bold pointer-events-none"
          style={{ fontSize: size === "large" ? "22px" : "17px" }}
        >
          {new Date().getFullYear()}
        </text>
        {activeFilter?.type !== FILTER_TYPES.YEAR && (
          <text
            x={160 * scale}
            y={168 * scale}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-primary pointer-events-none"
            style={{ fontSize: size === "large" ? "9px" : "7px" }}
          >
            reset
          </text>
        )}
      </svg>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const InteractiveAnnualWheel = ({
  onFilterChange,
  activeFilter = DEFAULT_FILTER,
  year = new Date().getFullYear(),
}) => {
  const [eventsData, setEventsData] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchAnnualWheel(year);
        setEventsData(data.months || {});
      } catch {
        // Fall back to mock data
      }
    };
    load();
  }, [year]);

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex justify-start w-full">
        <WheelSVG
          size="small"
          activeFilter={activeFilter}
          onFilterChange={onFilterChange}
          eventsData={eventsData}
        />
      </div>

      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2 text-xs text-muted-foreground pt-2 border-t border-border/40">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded border border-border shrink-0"
            style={{ backgroundColor: "hsl(var(--pastel-blue))" }}
          />
          <span>Months</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded border border-border shrink-0"
            style={{ backgroundColor: "hsl(var(--pastel-pink))" }}
          />
          <span>Events</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded border border-border shrink-0"
            style={{ backgroundColor: "hsl(var(--pastel-yellow))" }}
          />
          <span>Quarters</span>
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground">
        Click quarter or month to filter · Click centre to reset
      </p>
    </div>
  );
};

export default InteractiveAnnualWheel;
